import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Upload, X, ExternalLink } from 'lucide-react';
import { settingsApi } from '../../../api/endpoints/settings';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select } from '../../../components/ui/Select';
import { Toggle } from '../../../components/ui/Toggle';
import { useToast } from '../../../components/ui/Alert';
import { CURRENCIES } from '../../../lib/constants';
import { getStoreLogoUrl } from '../../../lib/formatters';
import type { StoreSettings } from '../../../types/settings';

const schema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  currency: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

// `label` is the English fallback; the Arabic name comes from
// `settingsGeneral.social.<key>` so the card isn't half-English in RTL.
// `phone` and `email` are stored as raw values (not URLs) and the storefront
// turns them into tel:/mailto: links.
const SOCIAL_PLATFORMS = [
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourstore' },
  { key: 'twitter', label: 'Twitter / X', placeholder: 'https://x.com/yourstore' },
  { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/yourstore' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@yourstore' },
  { key: 'snapchat', label: 'Snapchat', placeholder: 'https://snapchat.com/add/yourstore' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@yourstore' },
  { key: 'whatsapp', label: 'WhatsApp', placeholder: 'https://wa.me/966...' },
  { key: 'telegram', label: 'Telegram', placeholder: 'https://t.me/yourstore' },
  { key: 'discord', label: 'Discord', placeholder: 'https://discord.gg/invite-code' },
  { key: 'phone', label: 'Phone', placeholder: '+966 5xxxxxxxx' },
  { key: 'email', label: 'Email', placeholder: 'store@example.com' },
] as const;

// <input type="datetime-local"> works in local time with no zone and the
// format `YYYY-MM-DDTHH:mm`. Convert to/from the ISO string the API stores.
function isoToLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

interface Props { settings?: StoreSettings; storeId: string; }

export function GeneralTab({ settings, storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const [socialLinks, setSocialLinks] = useState<Record<string, string>>({});
  const [socialDirty, setSocialDirty] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoBase64, setLogoBase64] = useState<string | null>(null);

  const [ordersPaused, setOrdersPaused] = useState(false);
  const [pauseUntil, setPauseUntil] = useState('');
  const [pauseDirty, setPauseDirty] = useState(false);

  useEffect(() => {
    if (settings) {
      reset({ name: settings.name, description: settings.description, currency: settings.currency });
      // Normalize legacy array format [{app, link}] to object format for the UI
      const raw = settings.soicalMediaLinks;
      if (Array.isArray(raw)) {
        const obj: Record<string, string> = {};
        for (const item of raw as Array<{ app: string; link: string }>) {
          if (item.app && item.link) obj[item.app] = item.link;
        }
        setSocialLinks(obj);
      } else {
        setSocialLinks(raw ?? {});
      }
      setSocialDirty(false);
      setLogoPreview(settings.logo ? getStoreLogoUrl(settings.logo) : null);
      setLogoBase64(null);
      setOrdersPaused(settings.ordersPaused ?? false);
      setPauseUntil(isoToLocalInput(settings.ordersPauseUntil));
      setPauseDirty(false);
    }
  }, [settings, reset, storeId]);

  const handleLogoUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file || file.size > 2 * 1024 * 1024) return;
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        setLogoPreview(result);
        setLogoBase64(result);
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const mutation = useMutation({
    mutationFn: (data: FormValues) => {
      const payload: Record<string, unknown> = { ...data };
      // logoBase64 === null  → untouched, omit
      // logoBase64 === ''    → user removed the logo, send empty string
      // logoBase64 === data: → user uploaded a new logo
      if (logoBase64 !== null) payload.logo = logoBase64;
      return settingsApi.updateGeneral(storeId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', storeId] });
      toast('success', t('common.success'));
      setLogoBase64(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const socialMutation = useMutation({
    mutationFn: () => {
      // Convert object {key: url} to legacy array [{app, link}] format for the storefront
      const linksArray = Object.entries(socialLinks)
        .filter(([, link]) => !!link)
        .map(([app, link]) => ({ app, link }));
      return settingsApi.updateStore(storeId, { soicalMediaLinks: linksArray });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', storeId] });
      toast('success', t('common.success'));
      setSocialDirty(false);
    },
    onError: () => toast('error', t('common.error')),
  });

  const pauseMutation = useMutation({
    mutationFn: () => settingsApi.updateGeneral(storeId, {
      ordersPaused,
      // Only send a resume time while paused; clearing the toggle clears it too.
      ordersPauseUntil: ordersPaused ? localInputToIso(pauseUntil) : null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', storeId] });
      toast('success', t('common.success'));
      setPauseDirty(false);
    },
    onError: () => toast('error', t('common.error')),
  });

  const updateSocialLink = (key: string, value: string) => {
    setSocialLinks(prev => {
      const next = { ...prev };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
    setSocialDirty(true);
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-6">
        <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
          <h2 className="text-base font-semibold mb-4">{t('settings.storeInfo')}</h2>

          {/* Logo upload */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('settingsGeneral.storeLogo', 'Store Logo')}</label>
            <div className="flex items-center gap-4">
              {logoPreview ? (
                <div className="relative">
                  <img src={logoPreview} alt="" className="h-16 w-16 rounded-xl object-cover border border-border-default" />
                  <button
                    type="button"
                    onClick={() => { setLogoPreview(null); setLogoBase64(''); }}
                    className="absolute -top-1.5 -end-1.5 rounded-full bg-danger p-0.5 text-white hover:bg-danger/80"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleLogoUpload}
                  className="flex h-16 w-16 items-center justify-center rounded-xl border-2 border-dashed border-border-default hover:border-accent/50 transition-colors"
                >
                  <Upload className="h-5 w-5 text-text-muted" />
                </button>
              )}
              <button
                type="button"
                onClick={handleLogoUpload}
                className="text-sm text-accent hover:text-accent/80 transition-colors"
              >
                {logoPreview ? t('settingsGeneral.changeLogo', 'Change') : t('settingsGeneral.uploadLogo', 'Upload')}
              </button>
            </div>
          </div>

          {/* Store URL - labelled as Custom Domain once one is connected.
              Backend stores the custom domain on `settings.domain`; the
              `customDomain` alias exists for clarity. Check both. */}
          {storeId && (() => {
            const custom = settings?.customDomain || settings?.domain;
            return (
              <div>
                <label className="mb-1.5 block text-sm font-medium text-text-secondary">
                  {custom
                    ? t('settings.customDomain', 'Custom Domain')
                    : t('settingsGeneral.storeUrl', 'Store URL')}
                </label>
                <a
                  href={custom ? `https://${custom}` : `https://${storeId}.00stores.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
                >
                  {custom || `${storeId}.00stores.com`}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            );
          })()}

          <Input id="name" label={t('stores.storeName')} required error={errors.name?.message} {...register('name')} />
          <Textarea id="description" label={t('common.description')} rows={4} {...register('description')} />
          <Select
            id="currency"
            label={t('settings.currency')}
            options={CURRENCIES.map((c) => ({ value: c, label: c }))}
            {...register('currency')}
          />
        </section>
        <div className="flex justify-end">
          <Button type="submit" isLoading={mutation.isPending} disabled={!isDirty && logoBase64 === null}>{t('common.save')}</Button>
        </div>
      </form>

      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <div>
          <h2 className="text-base font-semibold">{t('settingsGeneral.pauseOrdersTitle', 'Pause Orders')}</h2>
          <p className="text-xs text-text-muted mt-1">
            {t('settingsGeneral.pauseOrdersDesc', 'Temporarily stop accepting new orders from customers. Your store stays online, but checkout is blocked.')}
          </p>
        </div>

        <Toggle
          label={t('settingsGeneral.pauseOrders', 'Pause incoming orders')}
          checked={ordersPaused}
          onChange={(v) => { setOrdersPaused(v); setPauseDirty(true); }}
        />

        {ordersPaused && (
          <>
            <Input
              type="datetime-local"
              label={t('settingsGeneral.pauseUntil', 'Resume automatically on (optional)')}
              value={pauseUntil}
              onChange={(e) => { setPauseUntil(e.target.value); setPauseDirty(true); }}
            />
            <p className="text-xs text-text-muted -mt-2">
              {pauseUntil
                ? t('settingsGeneral.pauseUntilHint', 'Orders resume automatically at the time above.')
                : t('settingsGeneral.pauseIndefiniteHint', 'Leave empty to stay paused until you turn this off.')}
            </p>
          </>
        )}

        <div className="flex justify-end">
          <Button onClick={() => pauseMutation.mutate()} isLoading={pauseMutation.isPending} disabled={!pauseDirty}>
            {t('common.save')}
          </Button>
        </div>
      </section>

      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold mb-4">{t('settingsGeneral.socialLinks', 'Social Media Links')}</h2>
        <p className="text-xs text-text-muted -mt-2">{t('settingsGeneral.socialLinksDesc', 'These links appear on your storefront footer and contact pages.')}</p>
        <div className="space-y-3">
          {SOCIAL_PLATFORMS.map(({ key, label, placeholder }) => (
            <Input
              key={key}
              label={t(`settingsGeneral.social.${key}`, label)}
              value={socialLinks[key] ?? ''}
              onChange={(e) => updateSocialLink(key, e.target.value)}
              placeholder={placeholder}
              dir="ltr"
            />
          ))}
        </div>
        <div className="flex justify-end pt-2">
          <Button onClick={() => socialMutation.mutate()} isLoading={socialMutation.isPending} disabled={!socialDirty}>
            {t('common.save')}
          </Button>
        </div>
      </section>
    </div>
  );
}
